// Sugalarm — voice bridge wrappers (cycle 16).
//
// NOTE (cycle 330): this build emits source maps, so comments in this file
// are readable by anyone inspecting the client. Nothing here — code OR
// comment — may name the upstream voice provider. The engine is a
// server-side detail behind `/api/tts/*`.
//
// `listVoices` returns the catalog the Voice section renders. Speech
// synthesis is POST-only (the request body carries the text + voice),
// so we can't point an `<audio>` element straight at the URL. Instead
// `fetchSpeechBlob` issues the POST through `authedFetch`, downloads
// the audio body as a Blob, returns an `objectURL` the caller assigns
// to the audio element, plus a `revoke()` helper the caller must
// invoke once playback ends — otherwise the blob URL leaks.

import { authedFetch, request } from "../lib/apiClient";
import type { ApiResult, VoiceListOut } from "../lib/types";

export function listVoices(
  localePrefix?: string,
): Promise<ApiResult<VoiceListOut>> {
  const qs = localePrefix
    ? `?locale_prefix=${encodeURIComponent(localePrefix)}`
    : "";
  return request<VoiceListOut>(`/api/tts/voices${qs}`);
}

export interface SpeechBlobHandle {
  url: string;
  revoke: () => void;
}

export async function fetchSpeechBlob(
  text: string,
  voice?: string | null,
): Promise<ApiResult<SpeechBlobHandle>> {
  const body: { text: string; voice?: string } = { text };
  if (voice && voice.trim()) body.voice = voice.trim();

  let res: Response;
  try {
    res = await authedFetch("/api/tts/speak", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch (e) {
    return {
      ok: false,
      error: {
        kind: "network",
        status: 0,
        message: e instanceof Error ? e.message : "network error",
      },
    };
  }
  if (!res.ok) {
    return {
      ok: false,
      error: {
        kind:
          res.status === 401
            ? "auth"
            : res.status === 422
              ? "validation"
              : res.status >= 500
                ? "server"
                : "unknown",
        status: res.status,
        message: res.statusText || `HTTP ${res.status}`,
      },
    };
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  return {
    ok: true,
    data: {
      url,
      revoke: () => URL.revokeObjectURL(url),
    },
  };
}

// ─── Cycle 309: TTS veracity-gate telemetry ─────────────────────────────
// Fire-and-forget report of a gate abort/rewrite/suppression so the fleet
// can be audited server-side (`sugalarm.tts.veracity` log tag). Bounded
// enum-ish strings only — never utterance text, never a member name.

export type VeracityEventKind =
  | "abort"
  | "rewrite_recovery"
  | "rewrite_stale"
  | "suppress_synthetic"
  | "suppress_resume"
  // Cycle 330: a scheduled announcement or a spoken reminder was skipped
  // because the member's voice quiet-hours window is open. Reuses this
  // existing seam rather than inventing a second reporting mechanism.
  // Mirrored in the server-side `VeracityEventIn` pattern.
  | "suppress_quiet_hours"
  // Cycle 474: cue + wake telemetry (`reportCueEvent` / `reportWakeEvent`).
  | "cue"
  | "wake";

export function reportVeracityEvent(
  kind: VeracityEventKind,
  surface: string,
  templateClassification?: string | null,
  contextClassification?: string | null,
): void {
  void request<void>("/api/tts/veracity_event", {
    method: "POST",
    body: {
      kind,
      surface,
      template_classification: templateClassification ?? null,
      context_classification: contextClassification ?? null,
    },
  }).catch(() => {
    /* telemetry only — never disturb the speech path */
  });
}

// ─── Cycle 474: cue + wake telemetry ────────────────────────────────────
// One bounded event per cue that SOUNDED or was SUPPRESSED by the one emit
// decision (lib/alarmCue.ts), and one per resume. Rides the cycle-309
// veracity seam (its own server-side bucket and log tag). Enum-ish strings
// and integers only — never a glucose value, a name, a token or a URL.
// Throttled here so a busy screen can never flood the seam: at most
// CUE_EVENTS_PER_MIN events a minute, and the same (kind, outcome) at most
// once per CUE_REPEAT_MS.
const CUE_EVENTS_PER_MIN = 20;
const CUE_REPEAT_MS = 10_000;
const sentAt: number[] = [];
const lastByKey = new Map<string, number>();

function throttleOk(key: string, now: number): boolean {
  while (sentAt.length > 0 && now - (sentAt[0] ?? now) > 60_000) sentAt.shift();
  if (sentAt.length >= CUE_EVENTS_PER_MIN) return false;
  const last = lastByKey.get(key);
  if (last != null && now - last < CUE_REPEAT_MS) return false;
  sentAt.push(now);
  lastByKey.set(key, now);
  return true;
}

function post(body: Record<string, unknown>): void {
  void request<void>("/api/tts/veracity_event", { method: "POST", body }).catch(() => {
    /* telemetry only */
  });
}

export function reportCueEvent(e: {
  surface: "phone" | "fixed";
  cueKind: string;
  outcome: string;
  inSettle: boolean;
  msSinceResume: number | null;
}): void {
  const now = Date.now();
  if (!throttleOk(`cue:${e.cueKind}:${e.outcome}:${e.inSettle}`, now)) return;
  post({
    kind: "cue",
    surface: e.surface,
    cue_kind: e.cueKind,
    outcome: e.outcome,
    in_settle: e.inSettle,
    ms_since_resume:
      e.msSinceResume == null ? null : Math.min(86_400_000, Math.max(0, Math.round(e.msSinceResume))),
  });
}

export function reportWakeEvent(e: {
  surface: "phone" | "fixed";
  reason: string;
  endedBy: string;
  settleMs: number;
  hiddenMs: number | null;
  freeze: boolean;
}): void {
  const now = Date.now();
  if (!throttleOk(`wake:${e.reason}:${e.endedBy}`, now)) return;
  post({
    kind: "wake",
    surface: e.surface,
    reason: e.reason,
    ended_by: e.endedBy,
    settle_ms: Math.min(600_000, Math.max(0, Math.round(e.settleMs))),
    hidden_ms: e.hiddenMs == null ? null : Math.min(604_800_000, Math.max(0, Math.round(e.hiddenMs))),
    freeze: e.freeze,
  });
}
